import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  EditorInputField,
  EditorSection,
  EditorTextField,
} from "@shared/components/editors/EditorFields";
import theme from "@shared/utils/themeColors";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Dropdown from "../../../shared/components/Dropdown/Dropdown";
import { preserveScrollPosition } from "../pages/TestEditor";
import {
  TestPassage,
  TestQuestion,
  TestSection,
} from "../types/test-detail.types";
import PassageEditor from "./PassageEditor";
import QuestionEditor from "./QuestionEditor";

interface TestSectionEditorProps {
  section: TestSection;
  sectionIndex: number;
  onUpdate: (section: TestSection) => void;
  onRemove: () => void;
  onMerge: (targetIndex: number) => void;
  onClone: () => void;
  otherSections: TestSection[];
  userId: string;
  isExpanded?: boolean;
  testId?: string; // Test ID for image storage
}

export default function TestSectionEditor({
  section,
  sectionIndex,
  onUpdate,
  onRemove,
  onMerge,
  onClone,
  otherSections,
  userId,
  isExpanded: controlledExpanded,
  testId,
}: TestSectionEditorProps) {
  // Initialize local state with the controlled value if provided
  const [localExpanded, setLocalExpanded] = useState(() =>
    controlledExpanded !== undefined ? controlledExpanded : true
  );

  // Update local state when controlled state changes
  useEffect(() => {
    if (controlledExpanded !== undefined) {
      setLocalExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  // Always use local state for rendering
  const isExpanded = localExpanded;
  const [showMergeOptions, setShowMergeOptions] = useState(false);
  const [passagesExpanded, setPassagesExpanded] = useState(true);
  // State to control passages expansion key
  const [passagesExpandCollapseKey, setPassagesExpandCollapseKey] = useState(0);
  // Track individual passage expansion states
  const [expandedPassageIds, setExpandedPassageIds] = useState<Set<string>>(
    () => {
      return new Set(passagesExpanded ? section.passages.map((p) => p.id) : []);
    }
  );

  // Function to update individual passage expansion state
  const updatePassageExpansion = (passageId: string, isExpanded: boolean) => {
    preserveScrollPosition(() => {
      setExpandedPassageIds((prev) => {
        const newSet = new Set(prev);
        if (isExpanded) {
          newSet.add(passageId);
        } else {
          newSet.delete(passageId);
        }
        return newSet;
      });
    });
  };

  // Optimized function to expand/collapse all passages
  const togglePassagesExpanded = (expanded: boolean) => {
    // Use the centralized utility function to preserve scroll position during updates
    preserveScrollPosition(() => {
      // Update expansion state
      setPassagesExpanded(expanded);

      // Update individual passage expansion states
      if (expanded) {
        // Add all passage IDs to the expanded set
        setExpandedPassageIds(new Set(section.passages.map((p) => p.id)));
      } else {
        // Clear the expanded set
        setExpandedPassageIds(new Set());
      }

      // Force re-render of child components to reset their local state
      setPassagesExpandCollapseKey((prev) => prev + 1);
    });
  };
  const [questionsExpanded, setQuestionsExpanded] = useState(true);

  // Get section name based on section number
  const getSectionName = (sectionNum: number) => {
    switch (sectionNum) {
      case 1:
        return "Reading";
      case 2:
        return "Writing";
      case 3:
        return "Math No Calculator";
      case 4:
        return "Math Calculator";
      case 5:
        return "Reading and Writing";
      case 6:
        return "Math";
      default:
        return `Section ${sectionNum}`;
    }
  };

  // Section type options for dropdown - using getSectionName to avoid duplication
  const sectionTypeOptions = [5, 6, 1, 2, 3, 4].map((num) => ({
    id: num.toString(),
    label: getSectionName(num),
    onClick: () => updateSectionNumber(num),
  }));

  // Update section number - Here we only notify the parent component
  const updateSectionNumber = (newSectionNumber: number) => {
    onUpdate({
      ...section,
      sectionNumber: newSectionNumber,
    });
  };

  // Update header
  const updateHeader = (field: "title" | "description", value: string) => {
    const updatedSection = {
      ...section,
      header: {
        ...section.header,
        [field]: value,
      },
    };
    onUpdate(updatedSection);
  };

  // Update introduction
  const updateIntroduction = (
    field: "title" | "description",
    value: string
  ) => {
    const updatedSection = {
      ...section,
      introduction: {
        ...section.introduction,
        [field]: value,
      },
    };
    onUpdate(updatedSection);
  };

  // Update directions
  const updateDirections = (directions: string) => {
    onUpdate({
      ...section,
      directions: directions,
    });
  };

  // Add passage
  const addPassage = () => {
    const newPassage: TestPassage = {
      id: uuidv4(),
      title: "",
      lines: [],
    };

    const updatedSection = {
      ...section,
      passages: [...section.passages, newPassage],
    };
    onUpdate(updatedSection);
  };

  // Update passage
  const updatePassage = (passageIndex: number, updatedPassage: TestPassage) => {
    const updatedPassages = [...section.passages];
    updatedPassages[passageIndex] = updatedPassage;

    const updatedSection = {
      ...section,
      passages: updatedPassages,
    };
    onUpdate(updatedSection);
  };

  // Remove passage
  const removePassage = (passageIndex: number) => {
    const updatedPassages = [...section.passages];
    updatedPassages.splice(passageIndex, 1);

    const updatedSection = {
      ...section,
      passages: updatedPassages,
    };
    onUpdate(updatedSection);
  };

  // Add question
  const addQuestion = () => {
    // Determine the number for the new question
    let newQuestionNumber = 1; // Default if no questions exist

    if (section.questions.length > 0) {
      // Find the maximum question number and add 1
      const maxQuestionNumber = Math.max(
        ...section.questions.map((q) => q.number)
      );
      newQuestionNumber = maxQuestionNumber + 1;
    }

    const newQuestion: TestQuestion = {
      id: uuidv4(),
      number: newQuestionNumber,
      text: "New question",
      options: [
        { id: "A", option: "A", text: "Option A" },
        { id: "B", option: "B", text: "Option B" },
        { id: "C", option: "C", text: "Option C" },
        { id: "D", option: "D", text: "Option D" },
      ],
      correctAnswer: "A",
    };

    const updatedSection = {
      ...section,
      questions: [...section.questions, newQuestion],
    };
    onUpdate(updatedSection);
  };

  // Update question
  const updateQuestion = (
    questionIndex: number,
    updatedQuestion: TestQuestion
  ) => {
    const updatedQuestions = [...section.questions];
    updatedQuestions[questionIndex] = updatedQuestion;

    const updatedSection = {
      ...section,
      questions: updatedQuestions,
    };
    onUpdate(updatedSection);
  };

  // Remove question
  const removeQuestion = (questionIndex: number) => {
    const updatedQuestions = [...section.questions];
    updatedQuestions.splice(questionIndex, 1);

    // If there are no questions left, don't need to renumber
    if (updatedQuestions.length === 0) {
      const updatedSection = {
        ...section,
        questions: [],
      };
      onUpdate(updatedSection);
      return;
    }

    // Find the minimum question number to preserve the numbering scheme
    const minQuestionNumber = Math.min(
      ...updatedQuestions.map((q) => q.number)
    );

    // Renumber remaining questions, starting from the minimum number
    const renumberedQuestions = updatedQuestions.map((q, idx) => ({
      ...q,
      number: minQuestionNumber + idx,
    }));

    const updatedSection = {
      ...section,
      questions: renumberedQuestions,
    };
    onUpdate(updatedSection);
  };

  // State to control questions expansion key
  const [questionsExpandCollapseKey, setQuestionsExpandCollapseKey] =
    useState(0);
  // Track individual question expansion states
  const [expandedQuestionIds, setExpandedQuestionIds] = useState<Set<string>>(
    () => {
      return new Set(
        questionsExpanded ? section.questions.map((q) => q.id) : []
      );
    }
  );

  // Function to update individual question expansion state
  const updateQuestionExpansion = (questionId: string, isExpanded: boolean) => {
    preserveScrollPosition(() => {
      setExpandedQuestionIds((prev) => {
        const newSet = new Set(prev);
        if (isExpanded) {
          newSet.add(questionId);
        } else {
          newSet.delete(questionId);
        }
        return newSet;
      });
    });
  };

  // Optimized function to expand/collapse all questions
  const toggleQuestionsExpanded = (expanded: boolean) => {
    // Use the centralized utility function to preserve scroll position during updates
    preserveScrollPosition(() => {
      // Update expansion state
      setQuestionsExpanded(expanded);

      // Update individual question expansion states
      if (expanded) {
        // Add all question IDs to the expanded set
        setExpandedQuestionIds(new Set(section.questions.map((q) => q.id)));
      } else {
        // Clear the expanded set
        setExpandedQuestionIds(new Set());
      }

      // Force re-render of child components to reset their local state
      setQuestionsExpandCollapseKey((prev) => prev + 1);
    });
  };

  // Handle drag end event for passages
  const handlePassagesDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // If dropped outside the list or at the same position
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      return;
    }

    // Use the scroll preservation utility to maintain scroll position
    preserveScrollPosition(() => {
      // Create a new array of passages
      const updatedPassages = [...section.passages];

      // Remove the dragged item from its original position
      const [movedPassage] = updatedPassages.splice(source.index, 1);

      // Insert the dragged item at its new position
      updatedPassages.splice(destination.index, 0, movedPassage);

      // Update the section with the new passages order
      const updatedSection = {
        ...section,
        passages: updatedPassages,
      };

      // Send the updated section to the parent component
      onUpdate(updatedSection);

      // Note: We don't need to update expandedPassageIds here because
      // we're using the passage IDs to track expansion state, not their indices
      // So the expansion state will be preserved automatically during reordering
    });
  };

  // Handle drag end event for questions
  const handleQuestionsDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // If dropped outside the list or at the same position
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      return;
    }

    // Use the scroll preservation utility to maintain scroll position
    preserveScrollPosition(() => {
      // Create a new array of questions
      const updatedQuestions = [...section.questions];

      // Remove the dragged item from its original position
      const [movedQuestion] = updatedQuestions.splice(source.index, 1);

      // Insert the dragged item at its new position
      updatedQuestions.splice(destination.index, 0, movedQuestion);

      // Find the minimum question number to preserve the numbering scheme
      const minQuestionNumber = Math.min(
        ...updatedQuestions.map((q) => q.number)
      );

      // Renumber questions after reordering, starting from the minimum number
      const renumberedQuestions = updatedQuestions.map((q, idx) => ({
        ...q,
        number: minQuestionNumber + idx,
      }));

      // Update the section with the new questions order
      const updatedSection = {
        ...section,
        questions: renumberedQuestions,
      };

      // Send the updated section to the parent component
      onUpdate(updatedSection);

      // Note: We don't need to update expandedQuestionIds here because
      // we're using the question IDs to track expansion state, not their indices
      // So the expansion state will be preserved automatically during reordering
    });
  };

  return (
    <Draggable draggableId={`section-${section.id}`} index={sectionIndex}>
      {(provided, snapshot) => (
        <div
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-md ${
            snapshot.isDragging ? "opacity-75" : ""
          }`}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          {/* Section Header */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 flex justify-between items-center relative">
            {/* Drag handle */}
            <div
              className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab"
              {...provided.dragHandleProps}
            >
              <svg
                className="w-5 h-5 text-gray-400"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm6-12a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setLocalExpanded(!localExpanded)}
                className={`mr-2 ml-6 ${theme.text.muted}`}
              >
                <svg
                  className={`w-5 h-5 transform ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </button>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                Section {sectionIndex + 1}{" "}
                {section.header?.title && ` - ${section.header.title}`}
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              {/* Section Type Dropdown */}
              <Dropdown
                horizontalAlign="left"
                verticalAlign="bottom"
                title="Section Type"
                items={sectionTypeOptions}
                trigger={
                  <div className="px-3 py-1 text-sm bg-indigo-500 hover:bg-indigo-600 text-white rounded-md flex items-center">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                    {getSectionName(section.sectionNumber)}
                  </div>
                }
                className="mr-2"
              />

              {/* Merge Button */}
              <div className="relative">
                <button
                  onClick={() => setShowMergeOptions(!showMergeOptions)}
                  className={`px-3 py-1 text-sm ${theme.bg.info} hover:bg-blue-600 text-white rounded-md flex items-center`}
                  disabled={otherSections.length === 0}
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                    ></path>
                  </svg>
                  Merge
                </button>
                {showMergeOptions && (
                  <div
                    className={`absolute right-0 mt-2 w-48 ${theme.bg.primary} rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700`}
                  >
                    <div className="py-1">
                      <p className={`px-4 py-2 text-sm ${theme.text.muted}`}>
                        Merge with:
                      </p>
                      {/* Previous section option */}
                      {sectionIndex > 0 && (
                        <button
                          onClick={() => {
                            onMerge(sectionIndex - 1);
                            setShowMergeOptions(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          Previous Section
                        </button>
                      )}
                      {/* Next section option */}
                      {sectionIndex < otherSections.length && (
                        <button
                          onClick={() => {
                            onMerge(sectionIndex + 1);
                            setShowMergeOptions(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          Next Section
                        </button>
                      )}
                      {sectionIndex === 0 &&
                        sectionIndex >= otherSections.length && (
                          <p
                            className={`px-4 py-2 text-sm ${theme.text.muted}`}
                          >
                            No adjacent sections available
                          </p>
                        )}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={onRemove}
                className={`px-3 py-1 text-sm ${theme.bg.error} hover:bg-red-600 text-white rounded-md flex items-center`}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  ></path>
                </svg>
                Remove
              </button>
              <button
                onClick={onClone}
                className={`px-3 py-1 text-sm ${theme.bg.success} hover:bg-green-600 text-white rounded-md flex items-center`}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  ></path>
                </svg>
                Clone
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="p-6">
              {/* Header Fields */}
              <EditorSection title="Header">
                <EditorInputField
                  label="Title"
                  value={section.header?.title || ""}
                  onChange={(value) => updateHeader("title", value)}
                  placeholder="Section title"
                />
                <EditorTextField
                  label="Description"
                  value={section.header?.description || ""}
                  onChange={(value) => updateHeader("description", value)}
                  placeholder="Section description"
                  rows={2}
                />
              </EditorSection>

              {/* Directions Fields */}
              <EditorSection title="Directions">
                <EditorTextField
                  value={section.directions || ""}
                  onChange={(value) => updateDirections(value)}
                  placeholder="Directions text"
                />
              </EditorSection>

              {/* Introduction Fields */}
              <EditorSection title="Introduction">
                <EditorInputField
                  label="Title"
                  value={section.introduction?.title || ""}
                  onChange={(value) => updateIntroduction("title", value)}
                  placeholder="Introduction title"
                />
                <EditorTextField
                  label="Description"
                  value={section.introduction?.description || ""}
                  onChange={(value) => updateIntroduction("description", value)}
                  placeholder="Introduction text"
                />
              </EditorSection>

              {/* Passages with Drag and Drop */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mr-3">
                      Passages ({section.passages.length})
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => togglePassagesExpanded(true)}
                        className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md flex items-center"
                        title="Expand All Passages"
                      >
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          ></path>
                        </svg>
                        Expand All
                      </button>
                      <button
                        onClick={() => togglePassagesExpanded(false)}
                        className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md flex items-center"
                        title="Collapse All Passages"
                      >
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 15l7-7 7 7"
                          ></path>
                        </svg>
                        Collapse All
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={addPassage}
                    className="px-3 py-1 text-sm bg-indigo-500 hover:bg-indigo-600 text-white rounded-md flex items-center"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      ></path>
                    </svg>
                    Add Passage
                  </button>
                </div>

                {/* Implement drag and drop for passages */}
                <DragDropContext onDragEnd={handlePassagesDragEnd}>
                  <Droppable droppableId={`section-${section.id}-passages`}>
                    {(provided) => (
                      <div
                        className="space-y-4"
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {section.passages.map((passage, passageIndex) => (
                          <Draggable
                            key={passage.id}
                            draggableId={`passage-${passage.id}`}
                            index={passageIndex}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="relative"
                              >
                                {/* Drag handle icon */}
                                <div
                                  className="absolute left-0 top-0 w-8 h-16 flex items-center justify-center cursor-grab"
                                  {...provided.dragHandleProps}
                                >
                                  <svg
                                    className="w-5 h-5 text-gray-400"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                  >
                                    <path d="M8 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm6-12a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                </div>
                                {/* Add padding to the left to make room for the drag handle */}
                                <div className="pl-8">
                                  <PassageEditor
                                    key={`passage-${passage.id}-${passagesExpandCollapseKey}`}
                                    passage={passage}
                                    passageIndex={passageIndex}
                                    onUpdate={(updatedPassage) =>
                                      updatePassage(
                                        passageIndex,
                                        updatedPassage
                                      )
                                    }
                                    onRemove={() => removePassage(passageIndex)}
                                    isExpanded={passagesExpanded}
                                    testId={testId}
                                    isIndividuallyExpanded={expandedPassageIds.has(
                                      passage.id
                                    )}
                                    onToggleExpansion={updatePassageExpansion}
                                  />
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {section.passages.length === 0 && (
                          <div
                            className={`text-center py-4 ${theme.text.muted}`}
                          >
                            No passages added yet. Click "Add Passage" to add
                            one.
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>

              {/* Questions */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mr-3">
                      Questions ({section.questions.length})
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => toggleQuestionsExpanded(true)}
                        className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md flex items-center"
                        title="Expand All Questions"
                      >
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          ></path>
                        </svg>
                        Expand All
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleQuestionsExpanded(false)}
                        className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md flex items-center"
                        title="Collapse All Questions"
                      >
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 15l7-7 7 7"
                          ></path>
                        </svg>
                        Collapse All
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={addQuestion}
                    className="px-3 py-1 text-sm bg-indigo-500 hover:bg-indigo-600 text-white rounded-md flex items-center"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      ></path>
                    </svg>
                    Add Question
                  </button>
                </div>
                {/* Implement drag and drop for questions */}
                <DragDropContext onDragEnd={handleQuestionsDragEnd}>
                  <Droppable droppableId={`section-${section.id}-questions`}>
                    {(provided) => (
                      <div
                        className="space-y-4"
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {section.questions.map((question, questionIndex) => (
                          <Draggable
                            key={question.id}
                            draggableId={`question-${question.id}`}
                            index={questionIndex}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="relative"
                              >
                                {/* Drag handle icon */}
                                <div
                                  className="absolute left-0 top-0 w-8 h-16 flex items-center justify-center cursor-grab"
                                  {...provided.dragHandleProps}
                                >
                                  <svg
                                    className="w-5 h-5 text-gray-400"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                  >
                                    <path d="M8 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm6-12a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                </div>
                                {/* Add padding to the left to make room for the drag handle */}
                                <div className="pl-8">
                                  <QuestionEditor
                                    key={`question-${question.id}-${questionsExpandCollapseKey}`}
                                    question={question}
                                    questionIndex={questionIndex}
                                    onUpdate={(updatedQuestion) =>
                                      updateQuestion(
                                        questionIndex,
                                        updatedQuestion
                                      )
                                    }
                                    onRemove={() =>
                                      removeQuestion(questionIndex)
                                    }
                                    userId={userId}
                                    isExpanded={questionsExpanded}
                                    isIndividuallyExpanded={expandedQuestionIds.has(
                                      question.id
                                    )}
                                    onToggleExpansion={updateQuestionExpansion}
                                  />
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {section.questions.length === 0 && (
                          <div
                            className={`text-center py-4 ${theme.text.muted}`}
                          >
                            No questions added yet. Click "Add Question" to add
                            one.
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}
